/* ═══════════════════════════════════════════════════════
   SPICY BOYS THEME — Global JavaScript
   ═══════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── PubSub ── */
  const subscribers = {};

  window.SpicyPubSub = {
    subscribe(event, callback) {
      if (!subscribers[event]) subscribers[event] = [];
      subscribers[event].push(callback);
      return () => {
        subscribers[event] = subscribers[event].filter((cb) => cb !== callback);
      };
    },
    publish(event, data) {
      if (subscribers[event]) {
        subscribers[event].forEach((cb) => cb(data));
      }
    },
  };

  /* ── Cart Drawer ── */
  function initCartDrawer() {
    const triggers = document.querySelectorAll('[data-cart-drawer-toggle]');
    const drawer = document.getElementById('CartDrawer');
    const backdrop = document.getElementById('CartDrawerBackdrop');
    const closeBtn = document.querySelector('[data-cart-drawer-close]');
    if (!drawer) return;

    function open(e) {
      if (e) e.preventDefault();
      drawer.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      // Focus trap
      const focusable = drawer.querySelector('button, [href], input');
      if (focusable) focusable.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    triggers.forEach((t) => t.addEventListener('click', open));
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
      }
    });

    window.SpicyPubSub.subscribe('cart:open', open);
    window.SpicyPubSub.subscribe('cart:close', close);
  }

  /* ── Cart AJAX ── */
  window.SpicyCart = {
    async getCart() {
      const res = await fetch(window.routes.cart_url + '.js');
      return res.json();
    },

    async addItem(id, quantity = 1) {
      const res = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }] }),
      });
      const data = await res.json();
      window.SpicyPubSub.publish('cart:updated', data);
      return data;
    },

    async updateItem(key, quantity) {
      const res = await fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const data = await res.json();
      window.SpicyPubSub.publish('cart:updated', data);
      return data;
    },

    /* Re-fetch the current page and swap in the freshly rendered drawer body,
       drawer title and header count. Leaves the drawer shell (and its bound
       close/backdrop listeners) untouched. */
    async refreshCartUI() {
      const res = await fetch(window.location.href, { headers: { Accept: 'text/html' } });
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      const swaps = [
        ['#CartDrawer .spicy-cart-drawer__body', 'innerHTML'],
        ['#CartDrawer .spicy-panel__title', 'textContent'],
      ];
      swaps.forEach(([sel, prop]) => {
        const next = doc.querySelector(sel);
        const cur = document.querySelector(sel);
        if (next && cur) cur[prop] = next[prop];
      });

      const nextBadge = doc.querySelector('[data-cart-count]');
      const curBadge = document.querySelector('[data-cart-count]');
      const cartLink = document.querySelector('.spicy-header__cart');
      if (nextBadge && curBadge) {
        curBadge.textContent = nextBadge.textContent;
      } else if (nextBadge && !curBadge && cartLink) {
        cartLink.appendChild(nextBadge);
      } else if (!nextBadge && curBadge) {
        curBadge.remove();
      }
    },
  };

  /* ── Empty cart (cart page + drawer): one click clears everything ── */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-empty]');
    if (!btn) return;
    btn.disabled = true;
    btn.textContent = 'Emptying…';
    try {
      await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
      if (document.querySelector('.main-cart-items')) {
        window.location.reload();
      } else {
        await window.SpicyCart.refreshCartUI();
      }
    } catch (err) {
      btn.disabled = false;
      btn.textContent = 'Empty cart';
    }
  });

  /* ── Quick remove (cart drawer) ── */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-remove]');
    if (!btn) return;
    e.preventDefault();

    btn.setAttribute('data-state', 'loading');
    try {
      await window.SpicyCart.updateItem(btn.getAttribute('data-cart-remove'), 0);
      await window.SpicyCart.refreshCartUI();
    } catch (err) {
      btn.removeAttribute('data-state');
    }
  });

  /* ── Quantity stepper (cart drawer) ──
     The count, badge and title update instantly from the cart response;
     the full drawer re-render (prices, subtotal) runs debounced in the
     background so rapid clicks stay snappy. */
  let stepperRefreshTimer = null;
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-qty-minus], [data-cart-qty-plus]');
    if (!btn) return;
    e.preventDefault();

    const key = btn.getAttribute('data-key');
    const qty = parseInt(btn.getAttribute('data-qty'), 10) || 0;
    const next = btn.hasAttribute('data-cart-qty-plus') ? qty + 1 : Math.max(0, qty - 1);
    const row = btn.closest('.spicy-cart-drawer__item');

    // Lock both buttons while the request is in flight — a click racing a
    // pending update would step from a stale quantity.
    const steps = row ? row.querySelectorAll('.spicy-cart-drawer__item-step') : [btn];
    steps.forEach((b) => b.setAttribute('data-state', 'loading'));

    try {
      const cart = await window.SpicyCart.updateItem(key, next);
      const line = (cart.items || []).find((i) => i.key === key);

      if (!line || !row) {
        // Line removed (or markup changed underneath us) — full re-render
        await window.SpicyCart.refreshCartUI();
        return;
      }

      const count = row.querySelector('.spicy-cart-drawer__item-count');
      if (count) count.textContent = line.quantity;
      row.querySelectorAll('[data-key]').forEach((b) => b.setAttribute('data-qty', line.quantity));

      const badge = document.querySelector('[data-cart-count]');
      if (badge) badge.textContent = cart.item_count;
      const title = document.querySelector('#CartDrawer .spicy-panel__title');
      if (title) title.textContent = title.textContent.replace(/\(\d+\)/, '(' + cart.item_count + ')');

      clearTimeout(stepperRefreshTimer);
      stepperRefreshTimer = setTimeout(() => window.SpicyCart.refreshCartUI(), 600);
    } catch (err) {
      window.SpicyCart.refreshCartUI();
    } finally {
      steps.forEach((b) => b.removeAttribute('data-state'));
    }
  });

  /* ── Quick add (product cards) ── */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();

    const label = btn.textContent;
    btn.setAttribute('data-state', 'loading');
    try {
      await window.SpicyCart.addItem(Number(btn.getAttribute('data-quick-add')), 1);
      await window.SpicyCart.refreshCartUI();
      // No drawer open — quick add stays frictionless. Confirm on the button.
      btn.textContent = 'Added ✓';
      setTimeout(() => { btn.textContent = label; }, 1500);
    } catch (err) {
      btn.textContent = 'Try again';
      setTimeout(() => { btn.textContent = label; }, 2000);
    } finally {
      btn.removeAttribute('data-state');
    }
  });

  /* ── Count-Up Animation ── */
  function initCountUp() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const elements = document.querySelectorAll('[data-count-up]');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseInt(el.getAttribute('data-count-up'), 10);
          if (isNaN(target)) return;

          let current = 0;
          const duration = 1000;
          const start = performance.now();

          function step(now) {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            current = Math.round(eased * target);
            el.textContent = current.toLocaleString();
            if (progress < 1) requestAnimationFrame(step);
          }

          requestAnimationFrame(step);
          observer.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ── Init ── */
  document.addEventListener('DOMContentLoaded', () => {
    initCartDrawer();
    initCountUp();
  });
})();
